import React, { ChangeEvent, ChangeEventHandler, KeyboardEventHandler, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useAppContext } from '../../../contexts/AppContext';

import sengiCoin from "../../../assets/sengi_coin.png"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { library } from '@fortawesome/fontawesome-svg-core'
import { fas, faChevronRight, faCopy, faMicrophoneLines, faPaperclip, faCaretLeft, faHeadphones, faImage, faFolderOpen, faCamera, faMicrophoneLinesSlash, faScrewdriverWrench, faGears } from '@fortawesome/free-solid-svg-icons'

import { useChatHistory } from '../../../hooks/ChatHooks';

// import { ApiServicesLLMs } from "../../apis/ApiServicesLLMs";
import { ChatMessage, ChatDataProps } from '../../../types/backend/SengiLabsTypes';
import { ChatComponent } from "./GenericChatComponent";
// import { PipelineFlow } from "../../components/AiPipelineFlow/PipelineFlow"
import { FlowBusinessSummary, FlowGPTProxy, FlowImageGenerator, FlowLargeLanguageModel, FlowMailToObject, FlowOCR, FlowPresentationGenerator, FlowSecureGPT, FlowSpeechRecognition, FlowStickerGenerator, FlowTextToSpeech, FlowTranslator } from "../../../pages/PageStructure/data";
import { PipelineData } from '../../AiPipelineFlow/PipelineTypes';
import { ApiPortalBackend } from '../../../apis/ApiPortalBackend';
import { useTranslation } from 'react-i18next';

import { SimpleToast } from '../../../components/ui/SimpleToast';
import { AiDisclosureNotice } from '../../../components/ui/AiDisclosureNotice';
import { useBootstrapTheme } from '../../../contexts/BootstrapThemeContext';
import { dataURLtoBlob } from '../../../utils/fileHandling';
import { Config } from '../../../config/Config';
import { fetchWithAuth } from '../../../utils/fetchWithAuth';
import { useTrackedRun } from '../../../hooks/useTrackedRun';
import { parseResponseToChatDataProps } from '../../../utils/chat/chatPropsParsing';
import { stateIconDone, stateIconError, stateIconWaiting } from '../../../types/ai/states';

library.add(fas, faChevronRight)

    //List of Languages
    /**
     *  language code is ​"de-DE" for language ​" Deutsch"
        language code is ​"en-US" for language ​" US English"
        language code is ​"en-GB" for language ​" UK English Female"​
        language code is ​"en-GB" for language ​" UK English Male"​
        language code is ​"es-ES" for language ​" español"
        language code is ​"es-US" for language ​" español de Estados Unidos"
        language code is ​"fr-FR" for language ​" français"​
        language code is ​"hi-IN" for language ​" हिन्दी Hindi"
        language code is ​"id-ID" for language ​" Bahasa Indonesia"
        language code is ​"it-IT" for language ​" italiano"
        language code is ​"ja-JP" for language ​" 日本語"
        language code is ​"ko-KR" for language ​" 한국의"
        language code is ​"nl-NL" for language ​" Nederlands"
        language code is ​"pl-PL" for language ​" polski"
        language code is ​"pt-BR" for language ​" português do Brasil"
        language code is ​"ru-RU" for language ​" русский"
        language code is ​"zh-CN" for language ​" ​普通话（中国大陆）"
        language code is ​"zh-HK" for language ​" ​粤語（香港）"
        language code is ​"zh-TW" for language ​" 國語（臺灣）"
     */
export interface ChatProps  {
  chatID: string;
  getChatMessages?: (messages:ChatMessage[]) => void;
};

export default function Chat({
  chatID,
  getChatMessages
}:ChatProps) {
  const { t } = useTranslation(['chat', 'common']);
  const context = useAppContext();
  const { effectiveTheme } = useBootstrapTheme();
  const [pipelineFlowData, setPipelineFlowData] = useState<PipelineData>(FlowGPTProxy);
  const [pipelineType, setPipelineType] = useState<string>("basis");
  const [pipelineName, setPipelineName] = useState<string>("new");
  const [state,setState]= useState<null|string>(null);
  const [jobID, setJobID] = useState<null|string>(null);
  const baseUrl = Config.json.AiServiceProxyUrl;
  const [responseState,setResponseState] = useState<null|string>(null);
  const { startRun, completeRun, failRun, insufficientCreditsDialog } = useTrackedRun();

  const [chatMessages, addMessage, addMessages, getLastMessage] = useChatHistory(chatID);
  const [chatInputString, setChatInputString] = useState<string>("");
  const [chatInputFiles, setChatInputFiles] = useState<ChatDataProps[]>([]);
  const [attachedFiles, setAttachedFiles] = useState<FormData | null>(null);
  const [attachedFileNames, setAttachedFileNames] = useState("");
  // Obergrenz-Annahme fuer die "bis zu X"-Vorschau: der einfache Chat-Request (siehe body in
  // sendMessage) setzt kein max_tokens/num_predict, wir preisen also den worst case mit dieser
  // angenommenen Ausgabe-Obergrenze. MUSS mit der echten Ausgabe-Deckelung des Backends
  // (gpt-oss:20b via cProxy) abgeglichen werden - siehe TASKS.md Kosten-Audit (2026-07-22).
  const CHAT_ASSUMED_MAX_OUTPUT_TOKENS = 1000;
  const [estimatedCost, setEstimatedCost] = useState<number|null>(0);
  const [estimatedIsUpperBound, setEstimatedIsUpperBound] = useState<boolean>(true);

  //speech recognition
  const [recognizing, setRecognizing] = useState(false);
  const [language, setLanguage] = useState('en-US'); // TODO: set language
  const [recognition, setRecognition] = useState<any |null>(null);

  //for taking pictures
  const [takenPicture, setTakenPicture] = useState<string | null>(null);

  //for adjusting textmessage
  const [accodionInput, setAccordionInput] = useState({secureGPT: false, aiService:''});
  const [showAccordion, setShowAccordion] = useState(false);
  const [inputSectionSpacing, setInputSectionSpacing] = useState(170);
  const [aiServiceList, setAiServiceList] = useState<string[]>([]);
  const [foundService, setFoundService] = useState(true);

  useEffect(() => {
    document.title = 'Chat';
    let navbarTitle = document.getElementById('navbar_title');
    if(navbarTitle){
        navbarTitle.textContent = `Chat - ${pipelineName}`;
    }
    calculateCost('');
    if (window.SpeechRecognition || window.webkitSpeechRecognition){
      //only supported by google chrome and microsoft edge
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      setRecognition(new SpeechRecognition()); 
    }
    loadAiServiceList();

    // clear on unmount
    return () => {
      if(timeoutChangedHandlerRef.current) clearTimeout(timeoutChangedHandlerRef.current);
    }
  },[])

  useEffect(() => {
    let navbarTitle = document.getElementById('navbar_title');
    if(navbarTitle){
        navbarTitle.textContent = `Chat - ${pipelineName}`;
    }
  }, [pipelineName]);

  useEffect(() => {
    let splitData = context.selectedPipeline.split("/");
    if(splitData.length > 1)
      setPipelineName(splitData[1]);

    switch(splitData[0]) {
      case "b":
        setPipelineType("basis");
        break;
      case "e":
        setPipelineType("extended");
        break;
      case "u":
        setPipelineType("user");
        break;
      case "c":
        setPipelineType("chat");
        break;
    }
    if(splitData[0] == "b" || splitData[0] == "e") {

      switch(splitData[1]) {
        case "GPTProxy":
          setPipelineFlowData(FlowGPTProxy);
          break;
        case "SecureGPT":
          setPipelineFlowData(FlowSecureGPT);
          break;

        case "LargeLanguageModel":
          setPipelineFlowData(FlowLargeLanguageModel);
          break;
        case "ImageGenerator":
          setPipelineFlowData(FlowImageGenerator);
          break;
        case "TextToSpeech":
          setPipelineFlowData(FlowTextToSpeech);
          break;
        case "SpeechRecognition":
          setPipelineFlowData(FlowSpeechRecognition);
          break;
        case "OCR":
          setPipelineFlowData(FlowOCR);
          break;
        case "Translator":
          setPipelineFlowData(FlowTranslator);
          break;


        case "MailToObject":
          setPipelineFlowData(FlowMailToObject);
          break;
        case "BusinessSummary":
          setPipelineFlowData(FlowBusinessSummary);
          break;
        case "PresentationGenerator":
          setPipelineFlowData(FlowPresentationGenerator);
          break;
        case "StickerGenerator":
          setPipelineFlowData(FlowStickerGenerator);
          break;
      }
    }

    if(splitData[0] == "u" ) {
      // User-Pipeline abrufen
    }
    
    if(splitData[0] == "c" ) {
      // Chat-Pipeline abrufen
    }
  }, [context.selectedPipeline]);

  useEffect(()=>{
    if(recognition){
      recognition.lang = language;
      recognition.interimResults = true; 
      recognition.maxAlternatives = 1; //one alternative per recognition

      //if speechrecording was successfully recognized
      recognition.onresult = async(event: any) => { //SpeechRecognitionEvent
        const transcript = Array.from(event.results) 
            .map((result: any) => result[0]) 
            .map(result => result.transcript) 
            .join('') 
        await setChatInputString(transcript);
      };
      //starting recognition
      recognition.onstart = async() => {
        await setRecognizing(true);
      };
      //end recognition
      recognition.onend = async() => {
        await setRecognizing(false);
      };
    }
  },[recognition]);

  // FUNCTIONS
  const startRecognition = () => {
    if(!recognizing && recognition){
      try{
        recognition.start();
      }
      catch{
      }
    }
  };
  const uploadFile = async(event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0]  : null;
    if(file){
      let newAttachedFiles: FormData = attachedFiles ?? new FormData();
      newAttachedFiles.append('file', file, file.name);
      let newAttachedFileNames = attachedFileNames !== '' ?  attachedFileNames+ ',' + file.name: file.name;
      let newChatInputFiles = [...chatInputFiles];
      let lastPosition = newChatInputFiles.length > 0 ? newChatInputFiles[newChatInputFiles.length-1].position : 0;
      if(event.target.id === 'fileuploadImage'){
        newChatInputFiles.push({
          type: 'image',
          element: URL.createObjectURL(file),
          position: lastPosition + 1
        });
        await setChatInputFiles(newChatInputFiles);
      }
      else if(event.target.id === 'fileuploadPDF'){
        newChatInputFiles.push({
          type: 'pdf',
          element: URL.createObjectURL(file),
          position: lastPosition + 1
        });
        await setChatInputFiles(newChatInputFiles);
      }
      await setAttachedFileNames(newAttachedFileNames);
      await setAttachedFiles(newAttachedFiles);
    }
  };
  const handleImageCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const reader = new FileReader();

      // Lade die Bilddatei und speichere sie als Daten-URL
      reader.onloadend = () => {
        setTakenPicture(reader.result as string); // Daten-URL speichern
      };
      reader.readAsDataURL(file); // Bilddatei als Base64-Daten-URL lesen
    }
  };

  const uploadImage=async() => {
    if(takenPicture){
      let newAttachedFiles: FormData = attachedFiles ?? new FormData();
      const blob = dataURLtoBlob(takenPicture);
      newAttachedFiles.append('file', blob, 'taken_picture');
      let newAttachedFileNames = attachedFileNames !== '' ?  attachedFileNames+ ',' + 'taken_picture': 'taken_picture';
      let newChatInputFiles = [...chatInputFiles];
      // Files will be positioned after input chat , user chat text will be always put at first position (=0)
      let lastPosition = newChatInputFiles.length > 0  ? newChatInputFiles[newChatInputFiles.length-1].position : 0;
      newChatInputFiles.push({
        type: 'image',
        element: URL.createObjectURL(blob),
        position: lastPosition+1
      });
      await setChatInputFiles(newChatInputFiles);
      await setAttachedFileNames(newAttachedFileNames);
      await setAttachedFiles(newAttachedFiles);
      await setTakenPicture(null);
    }
  };
  const onClickButtonAttachFiles = (id:string) => {
    const fileUpload = document.getElementById(id);
    if(fileUpload){
      fileUpload.click();
    }
  };
  const deleteAttachedFile = (idx:number) => {
    //delete file in formdata
    if(attachedFiles){
      let newFormData = attachedFiles;
      let values = newFormData.getAll('file');
      values.splice(idx,1);
      newFormData.set('file',values as any);

      setAttachedFiles(newFormData);
    }

    // delete file in names
    let newNames = attachedFileNames;
    let namesList = newNames.split(',');
    namesList.splice(idx,1);
    newNames = namesList.join(',');
    setAttachedFileNames(newNames);

    // delete file in chatdataprops
    let newChatDataProps = [...chatInputFiles];
    newChatDataProps.splice(idx,1);
    setChatInputFiles(newChatDataProps);
  };
  const readAloudText = (text: string) => {
    const reading = new SpeechSynthesisUtterance(text);
    reading.lang =  language;
    speechSynthesis.speak(reading);    
  };

  //SETTINGS
  const loadAiServiceList = () =>{
    //load list of services
    setAiServiceList(['gpt-4-pro','gpt-4-mini','dolly']);
    setAccordionInput(inputs =>({...inputs,['aiService']:'gpt-4-pro'}));
  };
  const handleChangeAccordionCheckboxInput = (event:React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const value = event.target.checked;
    setAccordionInput(input => ({ ...input, [name]: value }));
  };
  const handleChangeAiServiceInput = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const name = event.target.name;

    setAccordionInput(input => ({ ...input, [name]: value }));
    let aiService = aiServiceList.find(x => x === value);
    if(aiService){
      setFoundService(true);
    }
    else{
      setFoundService(false);
    }
  };

  //HANDLE CHAT INPUT
  // "bis zu X"-Vorschau: wir senden die rohe Prompt-Zeichenzahl + die angenommene Ausgabe-Obergrenze
  // an SL-CostsService (via /v1/runs/estimate). Der Service macht die chars->tokens-Umrechnung selbst
  // (kein lokales Zaehlen im Frontend - so nutzen Vorschau und der echte Abzug bei completeRun
  // denselben Tokenizer) und liefert estimateKind: "upper_bound" zurueck. Der reale Abzug erfolgt
  // spaeter aus den echten Tokens, ist praktisch immer kleiner als diese Obergrenze.
  const calculateCost = async (value:string) => {
    const estimate = await ApiPortalBackend.EstimateRunCost({
      type: 'llm',
      inputChars: value.length,
      maxOutputTokens: CHAT_ASSUMED_MAX_OUTPUT_TOKENS,
    });
    if (estimate && estimate.estimated) {
      setEstimatedCost(estimate.credits);
      setEstimatedIsUpperBound(estimate.estimateKind === 'upper_bound');
    } else {
      setEstimatedCost(null);
    }
  }

  const timeoutChangedHandlerRef = useRef<NodeJS.Timeout | null>(null);
  const onChatInputChangedHandler : ChangeEventHandler<HTMLTextAreaElement> = (event : ChangeEvent<HTMLTextAreaElement>) => {
    // calculate Cost here
    const value = event.target.value;
    setChatInputString(value);
    
    // Kostenvorschau entprellt neu berechnen. Der alte useCallback-im-Handler war ein
    // React-Hook-Fehlgebrauch (Hook in einem Event-Handler, Rueckgabe verworfen) und feuerte nie -
    // dadurch aktualisierte sich die Vorschau beim Tippen ueberhaupt nicht.
    if (timeoutChangedHandlerRef.current) clearTimeout(timeoutChangedHandlerRef.current);
    timeoutChangedHandlerRef.current = setTimeout(() => {
      calculateCost(value);
      timeoutChangedHandlerRef.current = null;
    }, 400);
  };

  const onChatInputKeyDownHandler = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) { // `!e.shiftKey` ermöglicht Shift+Enter für Zeilenumbruch (falls du ein <textarea> nutzt)
      event.preventDefault();                   // verhindert das "Absenden" als Form‑Submit oder Page‑Reload
      sendMessage();
    }
  };

  const addMessageToChat = (role:string,content: ChatDataProps[],inputFiles:ChatDataProps[],id:string) => {
    let newMessage : ChatMessage = {
      id: id,
      role: role, 
      contents: [
      ...content,
      ... inputFiles
    ],
      createdAt: new Date(),
      updatedAt: new Date()
    }
    //addMessage
    addMessage(newMessage);
    setResponseState('send');
  }

  const sendMessage = () => {
    let lastPosition = chatMessages.length > 0 && chatMessages[0].contents.length > 0 ? chatMessages[0].contents[chatMessages[0].contents.length-1].position+1 : 0; 
    let userMessage : ChatDataProps = {
      type: 'text',
      element:chatInputString,
      position: lastPosition
    }
    addMessageToChat('user',[userMessage],chatInputFiles,'');

    setChatInputString("");
    setAttachedFiles(null);
    setAttachedFileNames("");
    setChatInputFiles([]);
  }

  const onClickChatInputSendHandler : React.MouseEventHandler<HTMLDivElement> = (event : MouseEvent<HTMLDivElement>) => {
    sendMessage();
  };

  const callAiModel = async () => {
    let lastPosition = chatMessages.length > 0 && chatMessages[0].contents.length > 0 ? chatMessages[0].contents[chatMessages[0].contents.length-1].position+1 : 0;
    let bodyMessages: any[] = [];
    for(let chat of chatMessages){
      let textMessages = chat.contents.map(item => item.element.toString());
      bodyMessages.push({
        role: chat.role.toLowerCase(),
        content: textMessages.join("\n")
      })
    }
    //send messsage to ai
    const body = {
        model: "gpt-oss:20b",
        messages: bodyMessages
      };
    setResponseState('waiting');

    const runHandle = await startRun('llm', { estimatedCost: 0 });
    if (!runHandle) {
      // Either insufficient balance (dialog shown by useTrackedRun) or another failure - fail
      // closed, don't call cProxy without a tracked run.
      setResponseState('error');
      let errorContent:ChatDataProps =  {
        type: 'text',
        position: lastPosition,
        element: "❌ " + t('chat:error.requestFailed')
      }
      addMessageToChat('assistant',[errorContent],[],'');
      return;
    }

    try {
      const res = await fetchWithAuth(`${baseUrl}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        setResponseState('error');
        failRun(runHandle, `Chat request failed (HTTP ${res.status})`);
        let errorContent:ChatDataProps =  {
          type: 'text',
          position: lastPosition,
          element: "❌ " + t('chat:error.prefix') + (await res.text())
        }
        addMessageToChat('assistant',[errorContent],[],'');
        return;
      }

      const data = await res.json();
      const reply = data.reply || "No response.";
      const parsedReply: ChatDataProps[] = parseResponseToChatDataProps(reply);
      addMessageToChat('assistant',parsedReply,[],'');

      // vLLM's OpenAI-compatible response carries token usage in raw.usage - see SL_cProxy's
      // llm.py (raw=data passthrough). Falls back to 0 if the backend/model doesn't report it,
      // consistent with the documented "client-supplied cost" fallback for that case.
      const usage = data.raw?.usage;
      completeRun(runHandle, {
        inputTokens: usage?.prompt_tokens ?? 0,
        outputTokens: usage?.completion_tokens ?? 0,
      });

      setResponseState(null);
    } catch (err:any) {
        setResponseState('error');
        failRun(runHandle, `Chat request error: ${err.message}`);
        let errorContent:ChatDataProps =  {
          type: 'text',
          position: lastPosition,
          element: "❌ " + t('chat:error.connectionError') + err.message
        }
        addMessageToChat('assistant',[errorContent],[],'');
        return;;
    }
  };
  
  useEffect(()=> {
    if(chatMessages.length > 0){
      if(getChatMessages){
        getChatMessages([...chatMessages]);
      }
      if(chatMessages[chatMessages.length-1].role == 'user'){
        callAiModel();
      }
    }
  },[chatMessages]);

  /* -------------- 2️⃣ Auto‑Scroll bei Änderung des Chat‑Arrays -------------- */
  const chatMessagesContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Sicherstellen, dass das Element existiert
    const container = chatMessagesContainerRef.current;
    if (!container) return;
    
    // Gibt es überhaupt ein Kind?  Falls nicht, abbrechen.
    const lastChild = container.lastElementChild;
    if (!lastChild) return;

    // Scrollen zum Ende (bottom)
    lastChild.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest'
    });
  }, [chatMessages]); // <-- Auslöser: neue/aktualisierte Nachrichten

  return (
    <>
      <div className={`d-flex flex-column h-100 p-2`}
        style={{
            transition: 'margin-left 0.3s ease-in-out',
        }}
      >
        {insufficientCreditsDialog}
        <div className="row">
          <div aria-live="polite" aria-atomic="true" className="position-relative mt-2" style={{zIndex:500, paddingLeft:'45%'}}>
            <SimpleToast
                id='CopiedTextSuccesfull'
                header={false}
                bodyText='Copied!'
                bodyClass={`fw-bold ${ effectiveTheme === 'dark'? ' text-dark': 'text-light'}`}
                width='fit-content'
                classname={`position-absolute top-0 text-center ${ effectiveTheme === 'dark'? ' bg-secondary': 'bg-primary'}`}
            />
          </div>
        </div>
        <div className='row'>
          {responseState
            ? responseState === 'send'
                ? <p className='h4'>{t('chat:state.sending')}</p>
                : responseState === 'waiting'
                    ? <p className='h4'><span className="spin">{stateIconWaiting}</span> {t('chat:state.waiting')}</p>
                    : responseState === 'error'
                        ? <p className='h4'>{stateIconError} {t('chat:state.error')}</p>
                        : responseState === 'done'
                            ? <p className='h4'>{stateIconDone} {t('chat:state.done')}</p>
                            : <></>
            : <></>
        }
        </div>
        <div id="" className="flex-grow-1 overflow-auto">
          <div id="chatMessages" className="chat-area scroll-y" ref={chatMessagesContainerRef} >
            {chatMessages.map((entry : ChatMessage, index : number) => {
              if(entry.role == "user") {
                let chatContent: ChatDataProps[]= [...entry.contents];
                //sort by defined position from 0 to higher
                chatContent.sort((a,b) => a.position - b.position);
                return (
                  <div className="message-right">
                    <ChatComponent
                      items={chatContent}
                      id ={String(index)}
                      componentClass="message-right-content"
                      pdfHeight="auto"
                      pdfWidth="auto"
                    />
                  </div>
                )
              }
              else {
                let chatContent: ChatDataProps[]=[...entry.contents];
                //sort by defined position from 0 to higher
                chatContent.sort((a,b) => a.position - b.position);
                return (
                  <div className="message-left">
                    <ChatComponent
                      items={chatContent}
                      id ={String(index)}
                    />
                    <div className="float-start text-secondary pe-2 mb-2">
                      <button
                        type="button" 
                        className="btn btn-chat border-0 bg-transparent text-secondary p-0 m-0"
                        data-bs-toggle="tooltip" 
                        data-bs-placement="top" 
                        title={t('chat:title.readAloud')}
                      >
                        <FontAwesomeIcon
                          icon={faHeadphones}
                          onClick={e => readAloudText(chatContent.filter( x=> x.type === 'text').map(x => x.element).join(' '))}
                          size="1x"
                          className="pe-1"
                        />
                      </button>
                      <button
                        type="button" 
                        className="btn btn-chat border-0 bg-transparent text-secondary p-0 m-0"
                        data-bs-toggle="tooltip" 
                        data-bs-placement="top" 
                        title={t('chat:title.copy')}
                      >
                        <FontAwesomeIcon
                          icon={faCopy}
                          onClick={e => 
                            {
                              let content = chatContent.map(x => x.type === 'text' ? x.element : x.element.toString()).join('\n ');
                              navigator.clipboard.writeText(content);
                              let copiedToast = document.getElementById("CopiedTextSuccesfull");
                              if(!copiedToast) return;
                              copiedToast.style.display = 'block';
                              setTimeout(() => {
                                if(copiedToast){
                                  copiedToast.style.display = 'none';
                                }
                              }, 1000);
                            }
                          }
                          size="1x"
                          className="pe-1"
                        />
                      </button>
                    </div>
                  </div>
                );
              }
            })}
            {chatMessages.length > 0 && <AiDisclosureNotice />}
          </div>
        </div>
        <div id="section-chat-input" className="row">
          <div className="chat-input-grid">
            <div className="chat-options">
              <div className="dropdown dropstart">
                <button 
                  className="btn btn-chat bg-transparent text-secondary border-0" 
                  type="button" 
                  id="dropdownFileUpload"
                  data-bs-toggle="dropdown"
                  aria-haspopup="true" 
                  aria-expanded="false"
                >
                  <FontAwesomeIcon
                    icon={faPaperclip}
                    size="lg"
                    title={t('chat:title.attachFiles')}
                  />
                </button>
                <ul className="dropdown-menu dropdown-menu-dark ms-4 p-1 body-background border-dark rounded" aria-labelledby="dropdownFileUpload" style={{textAlignLast:'start'}}>
                  <li className="dropdown-item text-light p-2">
                    <FontAwesomeIcon
                      icon= {faCamera}
                      size="lg"
                      className="me-2"
                    />
                    <label htmlFor="fileuploadTokenImage">{t('chat:takePicture')} </label>
                    {/* <video id = 'fileuploadTokenImage' ref={videoRef} autoPlay style={{ maxWidth: '100%' }} />
                    <br />
                    <button onClick={handleCapture}>Bild aufnehmen</button>
                    <canvas ref={canvasRef} style={{ display: 'none' }} width={640} height={480} /> */}
                    <input
                      id = 'fileuploadTokenImage'
                      type= 'file'
                      accept='image/*'
                      capture="environment"
                      onChange={handleImageCapture}
                      hidden
                    />
                    {/* {takenPicture && (
                      <div>
                        <h3>Bildvorschau:</h3>
                        <img
                          src={takenPicture}
                          alt="Taken Picture"
                          style={{ maxWidth: '300px', marginTop: '20px' }}
                        />
                        <br />
                        <button onClick={uploadImage}>Upload</button>
                      </div>
                    )} */}
                  </li>
                  <li className="dropdown-item text-light p-2" onClick={e => onClickButtonAttachFiles('fileuploadImage')}>
                    <FontAwesomeIcon
                      icon= { faImage}
                      size="lg"
                      className="me-2"
                    />
                    <label htmlFor="fileuploadImage">{t('chat:attachImage')}</label>
                    <input
                        id = 'fileuploadImage'
                        type= 'file'
                        accept='image/png,image/jpg,image/jpeg'
                        onChange={event => uploadFile(event)}
                        hidden
                    />
                  </li>
                  <li className="dropdown-item text-light p-2" onClick={e => onClickButtonAttachFiles('fileuploadPDF')}>
                  <FontAwesomeIcon
                      icon= {faFolderOpen}
                      size="lg"
                      className="me-2"
                    />
                    <label htmlFor="fileuploadPDF">{t('chat:attachPdf')}</label>
                    <input
                        id = 'fileuploadPDF'
                        type= 'file'
                        accept='application/pdf'
                        onChange={event => uploadFile(event)}
                        hidden
                    />
                  </li>
                  {attachedFileNames.split(',').length > 0 && attachedFileNames.split(',')[0] !== ''

                    ? <>
                        <li><hr className="dropdown-divider my-1"/></li>
                        <li className="text-light pt-0">
                            <a className="dropdown-item" href="#"> 
                              <FontAwesomeIcon
                                icon={faCaretLeft}
                                size="xs"
                                className="me-1"
                              />
                              Delete File 
                            </a>
                            <ul className="dropdown-menu dropdown-submenu dropdown-menu-dark ms-4 p-1 body-background border-dark rounded" aria-labelledby="dropdownFileDelete">
                              {attachedFileNames.split(',').map((item,index)=> (
                                <li className="dropdown-item text-light " onClick={e => deleteAttachedFile(index)}>{item}</li>
                              ))}
                            </ul>
                        </li>
                      </>
                    : <></>
                  }
                </ul>
                </div>
            </div>
            <div className="chat-inputs">
              <div className="row p-2">
                  <div className="col-8 text-secondary">
                    {attachedFileNames}
                  </div>
                </div>
                <div className=" row chat-input border p-0">
                  <div className="input-group p-0">
                      <textarea 
                        id="chat-input" 
                        className="form-control chat-input border-0" 
                        aria-labelledby="chat-input-label" 
                        onChange={onChatInputChangedHandler} 
                        onKeyDown={onChatInputKeyDownHandler} 
                        style={{resize:'none'}}
                        value={chatInputString}>
                      </textarea>
                      <div className="d-grid" id="chat-input-label">
                        { recognition 
                          ? <button
                              disabled={recognizing}
                              onClick={startRecognition}
                              className="btn-chat bg-transparent text-secondary border-0 p-0 pe-1 mt-1"
                              data-bs-toggle="tooltip" 
                              data-bs-placement="top" 
                              title={t('chat:title.record')}
                              id="chat-input-label-audio"
                              style={{alignSelf:'start'}}
                            >
                              <FontAwesomeIcon
                                icon={faMicrophoneLines}
                                size="lg"
                              />
                            </button>
                          : <>
                            <button
                              disabled={true}
                              className="btn-chat bg-transparent text-secondary border-0 p-0 pe-1 mt-1"
                              data-bs-toggle="tooltip" 
                              data-bs-placement="top" 
                              title={t('chat:title.recordUnsupported')}
                              id="chat-input-label"
                              style={{alignSelf:'start'}}
                            >
                              <FontAwesomeIcon
                                icon={faMicrophoneLinesSlash}
                                size="lg"
                              />
                            </button>
                          </>
                        }
                        <button
                          className="btn-chat bg-transparent text-secondary border-0 p-0 pe-1 mb-1"
                          data-bs-toggle="tooltip" 
                          data-bs-placement="top" 
                          title={t('chat:title.adjust')}
                          id="chat-input-label-tools"
                          style={{alignSelf:'start'}}
                          onClick={(e => {
                            setInputSectionSpacing(!showAccordion ? 310 : 170);
                            setShowAccordion(!showAccordion);
                          })}
                        >
                          <FontAwesomeIcon
                            icon={faScrewdriverWrench}
                            size="lg"
                          />
                        </button>
                      </div>
                  </div>
                  
                </div>
                { showAccordion
                  ? <div className="mt-3">
                      <div className="h3">
                        <FontAwesomeIcon
                          icon={faGears}
                          size="1x"
                          className="me-2"
                        />
                        {t('chat:messageSettings')}
                      </div>
                      <div className="col-10 ms-5">
                        <div className='form-check'>
                          <input
                            className="form-check-input border-light"
                            type='checkbox'
                            name='secureGPT'
                            id='secureGPT'
                            checked={accodionInput.secureGPT}
                            onChange={event => handleChangeAccordionCheckboxInput(event)}
                          />
                          <label htmlFor='secureGPT' className='form-check-label'>
                            SecureGPT
                          </label>
                        </div>
                        <div className='mt-2 input-group'>
                          <label htmlFor='aiService' className={`input-group-text ${!foundService ? 'border-danger' : ''}`} >
                            {t('chat:aiService')}
                          </label>
                          <input 
                            className={`form-control rounded-end ${!foundService ? 'border-danger' : ''}`} 
                            list="datalistOptionsAiService" 
                            placeholder={t('chat:selectService')}
                            name="aiService"
                            value={accodionInput.aiService}
                            onChange={(event) => handleChangeAiServiceInput(event)}
                          />
                          <datalist id="datalistOptionsAiService">
                            {aiServiceList.map(item =>(
                              <option 
                                key={item} 
                                selected={item === accodionInput.aiService} 
                                value={item}
                                onClick={(e => {
                                  setAccordionInput(inputs =>({...inputs,['aiService']:item}));
                                  setFoundService(true);
                                })}
                              ></option>
                            ))}
                          </datalist>
                          
                        </div>
                        { !foundService
                          ? <div className='row'>
                              <p className='text-danger'>{t('chat:serviceNotFound')}</p>
                            </div>
                          : <></>

                        }
                      </div>
                    </div>
                  : <></> 
                }
              </div>
              <div className="chat-actions">
                <div className="" onClick={onClickChatInputSendHandler}>
                  <button
                    className="btn-chat bg-transparent border border-secondary rounded-circle text-secondary"
                    data-bs-toggle="tooltip" 
                    data-bs-placement="top" 
                    title={t('chat:title.send')}
                    disabled={responseState==='waiting'}
                  >
                    <FontAwesomeIcon icon={faChevronRight}/>
                  </button>
                </div>
                <span className="chat-input-estimated-coins" >{estimatedIsUpperBound ? t('chat:upToPrefix') + ' ' : ''}{estimatedCost ?? '—'} <img src={sengiCoin} alt='Sengi Coins' title="Sengi Coins" style={{height:"20px", marginLeft:"0px", marginRight:"0px", opacity: "50%" }}/></span>
              </div>
            </div>
          </div>
      </div>
    </>
  );
}
